import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, Save, Plus, Trash2, CreditCard, Sparkles, Phone, KeyRound, ArrowLeft, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPlan = () => ({
  id: `plan_${Date.now()}`,
  name: 'Nuovo Piano',
  price_monthly: 29,
  price_yearly: 290,
  included_elaborations: 500,
  duration_days: 30,
  features: ['Feature 1'],
  active: true,
});

export default function PlatformSettingsPage() {
  const { user, loading: authLoading, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/superadmin/platform-settings`, { withCredentials: true });
      setSettings(data);
    } catch (e) {
      toast.error('Errore caricamento impostazioni');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/account'); return; }
    if (!isSuperAdmin) { navigate('/'); return; }
    fetchSettings();
  }, [user, authLoading, isSuperAdmin, fetchSettings, navigate]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/superadmin/platform-settings`, settings, { withCredentials: true });
      setSettings(data);
      toast.success('Impostazioni salvate');
    } catch (e) {
      toast.error('Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const updatePlan = (idx, patch) => {
    const plans = [...(settings.ai_plans || [])];
    plans[idx] = { ...plans[idx], ...patch };
    setSettings({ ...settings, ai_plans: plans });
  };

  const addPlan = () => setSettings({ ...settings, ai_plans: [...(settings.ai_plans || []), emptyPlan()] });
  const removePlan = (idx) => {
    const plans = [...(settings.ai_plans || [])];
    plans.splice(idx, 1);
    setSettings({ ...settings, ai_plans: plans });
  };

  const updateProvider = (section, key, value) => {
    setSettings({
      ...settings,
      [section]: {
        ...settings[section],
        [key]: typeof settings[section][key] === 'object' && settings[section][key] !== null
          ? { ...settings[section][key], ...value }
          : value,
      },
    });
  };

  if (loading || !settings) {
    return <div className="px-4 pt-8 text-center"><Loader2 size={32} className="mx-auto animate-spin text-[#C25934]" /></div>;
  }

  return (
    <div data-testid="platform-settings-page" className="px-4 pt-4 pb-safe space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/superadmin')} className="p-2 rounded-full hover:bg-[#F0EBE1]" data-testid="back-btn">
          <ArrowLeft size={20} className="text-[#5C554F]" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            ⚙️ Impostazioni Piattaforma
          </h1>
          <p className="text-xs text-[#877E78]">Configura piani AI, chiavi API, provider</p>
        </div>
        <Button
          data-testid="save-settings-btn"
          onClick={saveSettings}
          disabled={saving}
          className="bg-[#C25934] hover:bg-[#A64B2B] text-white rounded-full h-10 px-4"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} className="mr-1.5" />}
          Salva
        </Button>
      </div>

      <Tabs defaultValue="plans">
        <TabsList className="w-full bg-[#F0EBE1] rounded-xl">
          <TabsTrigger value="plans" className="flex-1 rounded-lg data-[state=active]:bg-white" data-testid="tab-plans">
            <Sparkles size={14} className="mr-1.5" /> Piani AI
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex-1 rounded-lg data-[state=active]:bg-white" data-testid="tab-payments">
            <CreditCard size={14} className="mr-1.5" /> Pagamenti
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex-1 rounded-lg data-[state=active]:bg-white" data-testid="tab-ai-providers">
            <KeyRound size={14} className="mr-1.5" /> Provider AI
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex-1 rounded-lg data-[state=active]:bg-white" data-testid="tab-contact">
            <Phone size={14} className="mr-1.5" /> Contatti
          </TabsTrigger>
        </TabsList>

        {/* AI PLANS */}
        <TabsContent value="plans" className="space-y-3 mt-3">
          {(settings.ai_plans || []).map((p, idx) => (
            <div key={idx} data-testid={`plan-editor-${idx}`} className="bg-white rounded-xl border border-[#E8E3D9] p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Input value={p.name} onChange={e => updatePlan(idx, { name: e.target.value })} className="font-semibold" placeholder="Nome piano" data-testid={`plan-name-${idx}`} />
                <label className="flex items-center gap-1 text-xs text-[#5C554F] cursor-pointer">
                  <input type="checkbox" checked={p.active} onChange={e => updatePlan(idx, { active: e.target.checked })} data-testid={`plan-active-${idx}`} />
                  Attivo
                </label>
                <button onClick={() => removePlan(idx)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold text-[#877E78] uppercase">Mensile €</label>
                  <Input type="number" step="0.01" value={p.price_monthly} onChange={e => updatePlan(idx, { price_monthly: parseFloat(e.target.value) || 0 })} data-testid={`plan-price-monthly-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#877E78] uppercase">Annuale €</label>
                  <Input type="number" step="0.01" value={p.price_yearly || ''} onChange={e => updatePlan(idx, { price_yearly: parseFloat(e.target.value) || 0 })} data-testid={`plan-price-yearly-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#877E78] uppercase">Elaborazioni incluse</label>
                  <Input type="number" value={p.included_elaborations} onChange={e => updatePlan(idx, { included_elaborations: parseInt(e.target.value, 10) || 0 })} data-testid={`plan-included-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#877E78] uppercase">Durata (giorni)</label>
                  <Input type="number" value={p.duration_days} onChange={e => updatePlan(idx, { duration_days: parseInt(e.target.value, 10) || 30 })} data-testid={`plan-duration-${idx}`} />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold text-[#877E78] uppercase">Feature (una per riga)</label>
                <textarea
                  value={(p.features || []).join('\n')}
                  onChange={e => updatePlan(idx, { features: e.target.value.split('\n') })}
                  rows={3}
                  className="w-full px-2 py-1.5 rounded-lg border border-[#E8E3D9] text-xs resize-none outline-none focus:ring-2 focus:ring-[#C25934]"
                  data-testid={`plan-features-${idx}`}
                />
              </div>
            </div>
          ))}
          <Button onClick={addPlan} variant="outline" className="w-full h-10 rounded-xl border-[#C25934]/40 text-[#C25934]" data-testid="add-plan-btn">
            <Plus size={14} className="mr-1.5" /> Aggiungi piano
          </Button>
        </TabsContent>

        {/* PAYMENT PROVIDERS */}
        <TabsContent value="payments" className="space-y-3 mt-3">
          <ProviderCard
            title="Stripe"
            testid="stripe-card"
            enabled={settings.payment_providers?.stripe?.enabled}
            onToggle={v => updateProvider('payment_providers', 'stripe', { ...settings.payment_providers.stripe, enabled: v })}
            fields={[
              { label: 'Mode', key: 'mode', value: settings.payment_providers?.stripe?.mode || 'test', options: ['test', 'live'] },
              { label: 'Secret Key (sk_test_... / sk_live_...)', key: 'secret_key', value: settings.payment_providers?.stripe?.secret_key || '', type: 'password' },
              { label: 'Publishable Key (pk_test_... / pk_live_...)', key: 'publishable_key', value: settings.payment_providers?.stripe?.publishable_key || '' },
              { label: 'Webhook Secret', key: 'webhook_secret', value: settings.payment_providers?.stripe?.webhook_secret || '', type: 'password' },
            ]}
            onChange={(k, v) => updateProvider('payment_providers', 'stripe', { ...settings.payment_providers.stripe, [k]: v })}
          />
          <ProviderCard
            title="PayPal"
            testid="paypal-card"
            enabled={settings.payment_providers?.paypal?.enabled}
            onToggle={v => updateProvider('payment_providers', 'paypal', { ...settings.payment_providers.paypal, enabled: v })}
            fields={[
              { label: 'Mode', key: 'mode', value: settings.payment_providers?.paypal?.mode || 'sandbox', options: ['sandbox', 'live'] },
              { label: 'Client ID', key: 'client_id', value: settings.payment_providers?.paypal?.client_id || '' },
              { label: 'Client Secret', key: 'client_secret', value: settings.payment_providers?.paypal?.client_secret || '', type: 'password' },
            ]}
            onChange={(k, v) => updateProvider('payment_providers', 'paypal', { ...settings.payment_providers.paypal, [k]: v })}
          />
        </TabsContent>

        {/* AI PROVIDERS */}
        <TabsContent value="ai" className="space-y-3 mt-3">
          <div className="bg-white rounded-xl border border-[#E8E3D9] p-3 space-y-2">
            <label className="text-xs font-bold text-[#877E78] uppercase">Provider AI attivo</label>
            <select
              value={settings.ai_providers?.active || 'nano_banana'}
              onChange={e => setSettings({ ...settings, ai_providers: { ...settings.ai_providers, active: e.target.value } })}
              className="w-full px-3 py-2 rounded-lg border border-[#E8E3D9] bg-white"
              data-testid="ai-provider-active"
            >
              <option value="nano_banana">Nano Banana (Gemini via Emergent)</option>
              <option value="pebblely">Pebblely (flat fee)</option>
              <option value="fal_ai">Fal.ai Flux Kontext</option>
            </select>
          </div>
          <ProviderCard
            title="Nano Banana (Gemini 3.1 Flash Image)"
            testid="nb-card"
            enabled={settings.ai_providers?.nano_banana?.enabled}
            onToggle={v => updateProvider('ai_providers', 'nano_banana', { ...settings.ai_providers.nano_banana, enabled: v })}
            fields={[
              { label: 'Model ID', key: 'model', value: settings.ai_providers?.nano_banana?.model || 'gemini-3.1-flash-image-preview' },
            ]}
            onChange={(k, v) => updateProvider('ai_providers', 'nano_banana', { ...settings.ai_providers.nano_banana, [k]: v })}
            note="Usa EMERGENT_LLM_KEY dal .env — costo ~$0,10/immagine."
          />
          <ProviderCard
            title="Pebblely"
            testid="pebblely-card"
            enabled={settings.ai_providers?.pebblely?.enabled}
            onToggle={v => updateProvider('ai_providers', 'pebblely', { ...settings.ai_providers.pebblely, enabled: v })}
            fields={[
              { label: 'API Key', key: 'api_key', value: settings.ai_providers?.pebblely?.api_key || '', type: 'password' },
              { label: 'Plan', key: 'plan', value: settings.ai_providers?.pebblely?.plan || 'pro', options: ['lite', 'basic', 'pro', 'enterprise'] },
            ]}
            onChange={(k, v) => updateProvider('ai_providers', 'pebblely', { ...settings.ai_providers.pebblely, [k]: v })}
            note="Flat fee $39/mese — 200k img/giorno via API."
          />
          <ProviderCard
            title="Fal.ai Flux Kontext Pro"
            testid="fal-card"
            enabled={settings.ai_providers?.fal_ai?.enabled}
            onToggle={v => updateProvider('ai_providers', 'fal_ai', { ...settings.ai_providers.fal_ai, enabled: v })}
            fields={[
              { label: 'API Key', key: 'api_key', value: settings.ai_providers?.fal_ai?.api_key || '', type: 'password' },
              { label: 'Model', key: 'model', value: settings.ai_providers?.fal_ai?.model || 'fal-ai/flux-kontext-pro' },
            ]}
            onChange={(k, v) => updateProvider('ai_providers', 'fal_ai', { ...settings.ai_providers.fal_ai, [k]: v })}
            note="Pay-per-use ~$0,04/immagine (60% più economico di Nano Banana)."
          />
        </TabsContent>

        {/* CONTACT */}
        <TabsContent value="contact" className="space-y-3 mt-3">
          <div className="bg-white rounded-xl border border-[#E8E3D9] p-3 space-y-3">
            <div>
              <label className="text-[10px] font-bold text-[#877E78] uppercase">Email Supporto</label>
              <Input value={settings.contact?.support_email || ''} onChange={e => updateProvider('contact', 'support_email', e.target.value)} placeholder="supporto@photovendo.com" data-testid="support-email" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-[#877E78] uppercase">WhatsApp Supporto</label>
              <Input value={settings.contact?.support_whatsapp || ''} onChange={e => updateProvider('contact', 'support_whatsapp', e.target.value)} placeholder="+39 333 1234567" data-testid="support-whatsapp" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-[#877E78] uppercase">Telegram Supporto</label>
              <Input value={settings.contact?.support_telegram || ''} onChange={e => updateProvider('contact', 'support_telegram', e.target.value)} placeholder="@photovendo_support" data-testid="support-telegram" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-[#E8E3D9] p-3 space-y-3">
            <h3 className="text-sm font-bold text-[#2A2624]">Landing Page</h3>
            <div>
              <label className="text-[10px] font-bold text-[#877E78] uppercase">Hero title (lascia vuoto per default)</label>
              <Input value={settings.landing?.hero_title || ''} onChange={e => updateProvider('landing', 'hero_title', e.target.value)} placeholder="Trasforma le foto dal cellulare in un e-commerce." data-testid="landing-hero-title" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-[#877E78] uppercase">Hero subtitle</label>
              <Input value={settings.landing?.hero_subtitle || ''} onChange={e => updateProvider('landing', 'hero_subtitle', e.target.value)} placeholder="Tu scatti. L'AI crea..." data-testid="landing-hero-subtitle" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-[#877E78] uppercase">Video embed URL (YouTube/Vimeo)</label>
              <Input value={settings.landing?.video_embed_url || ''} onChange={e => updateProvider('landing', 'video_embed_url', e.target.value)} placeholder="https://www.youtube.com/embed/XXXX" data-testid="landing-video-url" />
              <p className="text-[10px] text-[#877E78] mt-1">Es: https://www.youtube.com/embed/VIDEO_ID?autoplay=1&rel=0</p>
            </div>
          </div>

          <div data-testid="image-search-settings" className="bg-white rounded-xl border border-[#E8E3D9] p-3 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#2A2624]">Ricerca immagini online (Google CSE)</h3>
              <button
                onClick={() => updateProvider('image_search', 'enabled', !settings.image_search?.enabled)}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${settings.image_search?.enabled ? 'bg-[#4A6B53]/10 text-[#4A6B53]' : 'bg-[#F0EBE1] text-[#877E78]'}`}
                data-testid="image-search-toggle"
              >
                {settings.image_search?.enabled ? <><Check size={12} /> Attivo</> : <><X size={12} /> Disattivo</>}
              </button>
            </div>
            <p className="text-[10px] text-[#877E78]">
              Abilita agli admin il pulsante "Cerca foto online" nel DraftEditor (Google Custom Search JSON API — 100 query/giorno gratis).
            </p>
            <div>
              <label className="text-[10px] font-bold text-[#877E78] uppercase">Google API Key</label>
              <Input
                type="password"
                value={settings.image_search?.google_api_key || ''}
                onChange={e => updateProvider('image_search', 'google_api_key', e.target.value)}
                placeholder="AIza..."
                data-testid="google-api-key"
              />
              <p className="text-[10px] text-[#877E78] mt-1">
                Ottienila su <a className="underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer">console.cloud.google.com</a> (abilita "Custom Search API").
              </p>
            </div>
            <div>
              <label className="text-[10px] font-bold text-[#877E78] uppercase">Search Engine ID (cx)</label>
              <Input
                value={settings.image_search?.google_cse_cx || ''}
                onChange={e => updateProvider('image_search', 'google_cse_cx', e.target.value)}
                placeholder="abc123..."
                data-testid="google-cse-cx"
              />
              <p className="text-[10px] text-[#877E78] mt-1">
                Crea su <a className="underline" href="https://programmablesearchengine.google.com/" target="_blank" rel="noreferrer">programmablesearchengine.google.com</a>, attiva "Cerca su tutto il web" + "Ricerca immagini".
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProviderCard({ title, testid, enabled, onToggle, fields, onChange, note }) {
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-[#E8E3D9] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-[#2A2624]">{title}</h3>
        <button onClick={() => onToggle(!enabled)} className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${enabled ? 'bg-[#4A6B53]/10 text-[#4A6B53]' : 'bg-[#F0EBE1] text-[#877E78]'}`}>
          {enabled ? <><Check size={12} /> Attivo</> : <><X size={12} /> Disattivo</>}
        </button>
      </div>
      {note && <p className="text-[10px] text-[#877E78]">{note}</p>}
      <div className="space-y-2">
        {fields.map(f => (
          <div key={f.key}>
            <label className="text-[10px] font-bold text-[#877E78] uppercase">{f.label}</label>
            {f.options ? (
              <select value={f.value} onChange={e => onChange(f.key, e.target.value)} className="w-full px-2 py-1.5 rounded-lg border border-[#E8E3D9] bg-white text-sm">
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <Input type={f.type || 'text'} value={f.value} onChange={e => onChange(f.key, e.target.value)} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
